import { useEffect, useMemo, useState } from "react"
import { NOT_NUMER_METHODS, PAYMENT_METHODS } from "src/constants/payments"
import useSortTable from "src/shared/hooks/useSortTable"
import { useFormatDate } from "src/shared/hooks/useFormatDate"
import { getCurrencyFormat } from "src/utils/string"
import { formatConcepto } from "../helpers/formatConcepto"
import { Movimiento } from "../interfaces/MovimientosCorte"
import { Concepto } from "../interfaces/concepto"
import { splitBySlash } from "src/shared/helpers/splitBySlash"

const ITEM_ALL = { value: "todos", valueToDisplay: "Todos" }

const PAYMENT_TYPES = {
    amex: PAYMENT_METHODS.amex,
    consumo_interno: PAYMENT_METHODS.consumoInterno,
    cortesia: PAYMENT_METHODS.cortesia,
    deposito_o_transferencia: PAYMENT_METHODS.depositoOTransferencia,
    efectivo: PAYMENT_METHODS.efectivo,
    visa_o_mastercard: PAYMENT_METHODS.visaOMasterCard,
    love_points: PAYMENT_METHODS.lovePoints,
    mixto: { value: "mixto", label: "Mixto" },
}

type Keys = keyof typeof PAYMENT_TYPES

export function useHeader(data: any[], categoriasSeleccionadas?: string[]) {
    const getFilterOptions = (key: string) => {
        const options: any[] = []

        data?.forEach((item) => {
            let value = item?.[key]
            if (key === "concepto") {
                if (
                    categoriasSeleccionadas &&
                    categoriasSeleccionadas.length > 0 &&
                    !categoriasSeleccionadas.includes(item.categoria)
                )
                    return
                if (value?.includes("/")) {
                    value = value.split("/")[0].trim()
                }

                if (value === "reserva") value = "Propina reserva"
                if (value === "renta") value = "Propina renta"
            }

            if (value) {
                const find = options.find((item) => item?.value === value)
                if (!find) options.push({ value, valueToDisplay: value })
            }
        })

        return options.length > 0 ? [ITEM_ALL, ...options] : []
    }

    const getPaymentItems = (keys: Keys[]) => {
        return keys.map((key) => {
            const item = PAYMENT_TYPES[key]
            return { value: item?.value, valueToDisplay: item?.label }
        })
    }

    const headers = useMemo(() => {
        const base = [
            { value: "#" },
            {
                value: "Categoría",
                filterMenu: getFilterOptions("categoria"),
            },
            {
                value: "Concepto",
                filterMenu: getFilterOptions("concepto"),
            },
            {
                value: "Monto",
                sort: true,
            },
            {
                value: "Método de pago",
                filterMenu: [
                    ITEM_ALL,
                    ...getPaymentItems([
                        "visa_o_mastercard",
                        "amex",
                        "efectivo",
                        "cortesia",
                        "deposito_o_transferencia",
                        "consumo_interno",
                        "mixto",
                    ]),
                ],
            },
            {
                value: "Fecha",
                sort: true,
            },
            {
                value: "Responsable",
                filterMenu: getFilterOptions("responsable"),
            },
            { value: "Acciones" },
        ]

        return base
    }, [data, categoriasSeleccionadas])

    return headers
}

export function useRows(data: Movimiento[]) {
    const { formatCustomDate } = useFormatDate()
    const [rows, setRows] = useState<any[]>([])

    const getPaymentKey = (key: string) => {
        if (key) {
            const items = key.split(", ")
            const clear = [...new Set([...items])]
            if (clear.length > 1) {
                return "mixto"
            } else {
                const cleanItem = clear[0].split(" / ")

                return cleanItem[0] || ""
            }
        } else {
            return ""
        }
    }

    const isValidEdicion = (tipo_pago: string) => {
        const valid_keys = ["efectivo", "visa_o_mastercard", "amex"]
        const items = tipo_pago?.split(", ") || []
        const clear = [...new Set([...items])]
        const filter = clear.filter((item) => {
            const keys = item.split(" / ")
            const pago = keys?.[0] || ""
            return valid_keys.includes(pago)
        })

        return filter.length > 0
    }

    const getRowFormat = (list: Movimiento[]) => {
        const orderList =
            list.sort((a, b) => new Date(b.fecha_registro).getTime() - new Date(a.fecha_registro).getTime()) || []
        return orderList.map(
            ({
                folio_venta = 0,
                categoria = "",
                concepto = "",
                total = 0,
                tipo_pago = "",
                fecha_registro = "",
                responsable = "",
                ticket_id = "",
                descuento = "",
                total_descuento = "",
                total_original = ""
            }) => {
                const payments = splitBySlash(tipo_pago)
                const metodo_pago = payments.length > 1
                                        ? "Mixto"
                                        : PAYMENT_TYPES?.[getPaymentKey(payments?.[0]?.[0])]?.label || "N/A"
                const descuento_aplicado = descuento && descuento !== "-" ? descuento : ""
                const descuento_label = descuento_aplicado ? descuento_aplicado.includes("%") ? descuento_aplicado : getCurrencyFormat(Number(descuento_aplicado))  : ""

                const total_movimiento = descuento_aplicado ? Number(total_descuento || total) : Number(total_original || total)
                const total_origen_movimiento = Number(total_original || 0)
                const allowEditar = isValidEdicion(tipo_pago || "") && !["Propina", "Gasto"].includes(categoria)
                const allowCancelar =  isValidEdicion(tipo_pago || "") &&  ["Estancia", "Horas extra", "Personas extra", "Hospedaje extra"].includes(`${categoria || ""}`)

                const base = [
                    { value: folio_venta },
                    { value: categoria },
                    {
                        value: formatConcepto(
                            concepto === "reserva"
                                ? "Propina reserva"
                                : concepto === "renta"
                                ? "Propina renta"
                                : (concepto as Concepto)
                        ),
                    },
                    {
                        value: {
                            total_movimiento: getCurrencyFormat(total_movimiento),
                            total_original: getCurrencyFormat(total_origen_movimiento),
                            descuento: descuento_label
                        }, 
                        fromHeaderSort: "Monto",
                        sortValue: total_movimiento 
                    },
                    {
                        value: (
                            <div style={{ display: "flex", flexDirection: "column" }}>
                                <span>
                                    {metodo_pago}
                                </span>
                                {(payments.length > 1 || !NOT_NUMER_METHODS.includes(payments?.[0]?.[0])) && (
                                    <div style={{ display: "flex", flexWrap: "wrap" }}>
                                        {payments.map((p, index) => (
                                            <span key={index} style={{ color: "var(--placeholder)" }}>
                                                {NOT_NUMER_METHODS.includes(p[0])
                                                    ? PAYMENT_TYPES?.[getPaymentKey(p[0])]?.label
                                                    : p[1]}
                                                {index < payments.length - 1 ? ",\u00A0" : ""}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    {
                        value: fecha_registro ? formatCustomDate(new Date(fecha_registro), "MMM, D YYYY hh:mm A") : "N/A",
                        fromHeaderSort: "Fecha",
                        sortValue: fecha_registro,
                    },
                    { value: `${responsable}` },
                    {
                        value: {
                            ticket_id,
                            tipo_pago: getPaymentKey(tipo_pago),
                            cancelacion: categoria?.includes("Cancelación"),
                            editar: allowEditar,
                            cancelar: allowCancelar,
                            total: Number(total_movimiento || 0),
                            tipo: categoria || "",
                            folio_venta,
                        },
                    },
                ]
                return {
                    goTo: JSON.stringify({  ticket_id, data: { ...(orderList.find((i)=>i.ticket_id === ticket_id) || {}), total_movimiento, total_origen_movimiento, descuento_label, metodo_pago, allowCancelar, allowEditar, tipo: categoria || "", cancelacion: categoria?.includes("Cancelación"), tipo_pago: getPaymentKey(tipo_pago), pago_origen: tipo_pago } }),
                    value: base,
                }
            }
        )
    }

    useEffect(() => {
        if (data?.length > 0) {
            setRows(getRowFormat([...data]))
        } else {
            setRows([])
        }
    }, [])

    const handleSort = (params) => {
        const sortData = useSortTable({ sortState: params?.sort || null, sortedData: rows, i: params?.idx || 0 })
        setRows(sortData)
    }

    const handleFilter = (params: any[]) => {
        const getKeys = (id) => clearParams.filter(({ idx }) => idx === id).map(({ filter }) => filter)
        const findValue = (keys: string[], value: string) => (keys.length > 0 ? keys.includes(value) : true)

        const keyAll = params.filter(({ filter }) => filter === "todos")
        const clearParams =
            keyAll.length === 0 ? params : params.filter(({ idx }) => keyAll.find((item) => item.idx !== idx))
        if (clearParams.length > 0) {
            const results: any[] = []
            const categoriaKeys = getKeys(1)
            const conceptoKeys = getKeys(2)
            const pagoKeys = getKeys(4)
            const responsableKeys = getKeys(6)

            data.forEach((item) => {
                const conceptoKey = item?.concepto?.includes("/") ? item.concepto.split("/")[0].trim() : item.concepto
                const finalConceptoKey =
                    conceptoKey === "reserva"
                        ? "Propina reserva"
                        : conceptoKey === "renta"
                        ? "Propina renta"
                        : conceptoKey
                if (
                    findValue(categoriaKeys, item?.categoria) &&
                    findValue(conceptoKeys, finalConceptoKey) &&
                    findValue(pagoKeys, getPaymentKey(item?.tipo_pago)) &&
                    findValue(responsableKeys, item?.responsable)
                ) {
                    results.push(item)
                }
            })

            setRows(getRowFormat([...results]))
        } else {
            setRows(getRowFormat([...data]))
        }
    }

    return { rows, handleSort, handleFilter }
}